import { get } from 'svelte/store';

import { apiClient, ApiError } from '$lib/api/client';
import { authHeader } from '$lib/stores/auth';
import { skeleton, type IndexResponse } from '$lib/api/selection';
import { seatSelection } from '$lib/stores/seatselection';
import { queryFilters } from '$lib/stores/selection';
import type { CompanyWithContacts } from '$lib/types/api';

/**
 * The one-box search.
 *
 * Sends what somebody typed to `/deep-iq/search`, which looks in a company's
 * register identifier, its name, its web address, the segment it was classified
 * into, its registered purpose and what its website offers. See
 * `docs/refactor/full-scope-search.md`.
 *
 * # It answers in the same shape a filtered selection does
 *
 * Deliberately, and it is what makes this cheap: the response is an index of
 * skeleton rows, so it goes into the same stores a filter search fills and the
 * results screen draws it without knowing which of the two produced it. The
 * rows are then hydrated a block at a time by the machinery that was already
 * there.
 */

/** Where a company's terms were found, as the API reports it. */
export type SearchSource = 'identifier' | 'name' | 'domain' | 'segment' | 'purpose' | 'website';

interface SearchResponse extends IndexResponse {
  sources?: Record<string, SearchSource[]>;
}

/** What a search found. */
export interface SearchOutcome {
  companies: CompanyWithContacts[];

  /** How many matched, which is more than `companies` holds when capped. */
  total: number;
  capped: boolean;

  /** Why each company is in the answer, by company identifier. */
  sources: Record<string, SearchSource[]>;
}

/** One industry segment the query named. */
export interface SegmentSuggestion {
  /** The industry centroid, as a plain number — a selection is built from it. */
  id: number;
  name: string;
  /** How many companies are classified into it. */
  companies: number;
}

/** One company the query named. */
export interface CompanySuggestion {
  id: string;
  name: string;

  /**
   * Its web address, absent for most companies.
   *
   * What tells two answers apart. Swiss company names repeat, and four rows
   * reading "Muster AG" are four rows a customer cannot choose between.
   */
  domain?: string;

  /** The segment it was classified into — the taxonomy, not its NOGA code. */
  segment?: SegmentSuggestion;
}

/** What a query offers before anybody commits to a search. */
export interface Suggestions {
  segments: SegmentSuggestion[];
  companies: CompanySuggestion[];
  /** How many companies the full search would find, or -1 when it is capped. */
  total_companies: number;
}

/**
 * Ask what a query would find, without running the search.
 *
 * Two bounded queries and no state written: nothing here decides that the
 * customer wants a result set. That happens when they click.
 */
export async function suggest(query: string): Promise<Suggestions> {
  const trimmed = query.trim();
  if (!trimmed) return { segments: [], companies: [], total_companies: 0 };

  try {
    const response = await apiClient.post<Suggestions>(
      '/deep-iq/suggest',
      { query: trimmed },
      { headers: authHeader() }
    );
    return response.data;
  } catch (error) {
    throw refusal(error);
  }
}

/** A search the server refused, with the code it refused under. */
export class SearchRefused extends Error {
  constructor(
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'SearchRefused';
  }
}

/**
 * Run a search and leave the results where the app already looks for them.
 *
 * # Nothing calls this today
 *
 * The members' home used to, with one box over every scope. That box is now
 * two: the top bar answers "take me to X" through `suggest`, and the offering
 * search — "find me everyone who does Y" — will call this with
 * `scopes: ['purpose', 'website']`. Kept rather than deleted because it is the
 * second half of a split already half-built, and because it is the one place
 * that knows the order a selection has to be seated in.
 *
 * See docs/refactor/find-and-build.md.
 *
 * # Why this writes the stores rather than returning and letting a caller do it
 *
 * Because getting it wrong is silent. A search that filled `queryResult` and
 * forgot `mixedResults` shows nothing; one that forgot `resetHydration` shows
 * the previous search's rows under the new search's names. Both are states the
 * app has been in before. Doing it in one place means there is one order to get
 * right.
 */
export async function runSearch(query: string, scopes?: SearchSource[]): Promise<SearchOutcome> {
  const trimmed = query.trim();
  if (!trimmed) {
    throw new SearchRefused('SEARCH_TERM_TOO_SHORT', 'Bitte geben Sie einen Suchbegriff ein.');
  }

  let data: SearchResponse;
  try {
    const response = await apiClient.post<SearchResponse>(
      '/deep-iq/search',
      {
        query: trimmed,
        ...(scopes?.length ? { scopes } : {}),
        contact_mode: get(queryFilters).contact_mode || 'single'
      },
      { headers: authHeader() }
    );
    data = response.data;
  } catch (error) {
    throw refusal(error);
  }

  const companies = (data.companies ?? []).map(skeleton);

  // The ordering this depends on lives in `seatSelection`, which a saved list
  // now uses too. It was written here, with a comment explaining why it had to
  // be in one place; it moved when there were two callers.
  seatSelection(companies, { total: data.total ?? 0, capped: data.capped ?? false });

  return {
    companies,
    total: data.total ?? 0,
    capped: data.capped ?? false,
    sources: data.sources ?? {}
  };
}

/**
 * Turn a rejected search into something with a code on it.
 *
 * The server refuses for reasons a customer can act on — too short, too many
 * words, an unknown scope, or asking to look a company up by name without an
 * account — and each carries its own code. A bare "search failed" would throw
 * all four away.
 */
function refusal(error: unknown): SearchRefused {
  if (error instanceof ApiError) {
    const body = error.data as { code?: string; error?: string } | undefined;
    if (body?.code) {
      return new SearchRefused(body.code, body.error ?? 'Die Suche wurde abgelehnt.');
    }
  }
  return new SearchRefused('SEARCH_FAILED', 'Die Suche konnte nicht ausgeführt werden.');
}
